import { Menu, Text, Tooltip } from '@mantine/core';
import { LeadStatus } from '@bariendo/lib/active-lead';
import { MouseEvent, ReactElement } from 'react';
import { ORBIT_GRAY } from '@/theme/colors';
import { LEAD_STATUS_OPTION, LEAD_STATUS_OPTIONS } from './leadStatusOptions';
import './LeadStatusMenu.css';

/**
 * A lead's status as a dot and label that opens Active / Hold / Lost. The same
 * control in the patient panel and in a table's Status cell; clicks don't reach
 * a clickable row behind it.
 */
export interface LeadStatusMenuProps {
  status: LeadStatus;
  onChange: (status: LeadStatus) => void;
  /** `panel` is 13px on the panel's value line; `cell` is 12px for a table. */
  size?: 'panel' | 'cell';
  disabled?: boolean;
  /** On hover; when disabled, say why. */
  tooltip?: string;
}

function stopRowClick(event: MouseEvent): void {
  event.stopPropagation();
}

export function LeadStatusMenu({
  status,
  onChange,
  size = 'panel',
  disabled = false,
  tooltip,
}: LeadStatusMenuProps): ReactElement {
  const current = LEAD_STATUS_OPTION[status];
  // Panel matches the panel's value text, so it sits on the same line as its neighbours.
  const font = size === 'panel' ? { fontSize: 14, lineHeight: '20px' } : { fontSize: 12, lineHeight: '16px' };

  return (
    <Menu position="bottom-start" offset={6} disabled={disabled} withinPortal>
      <Tooltip label={tooltip} disabled={!tooltip} withArrow position="top-start">
        {/* A disabled button fires no hover events, so the tooltip hangs off this wrapper. */}
        <span style={{ display: 'inline-flex', verticalAlign: 'top' }} onClick={stopRowClick}>
          <Menu.Target>
            <button
              type="button"
              className={`lead-status-button${size === 'panel' ? ' is-panel' : ''}`}
              style={{ ...font, color: current.textColor }}
              disabled={disabled}
              aria-label={`Lead status: ${current.label}`}
            >
              <span className="lead-status-dot" style={{ background: current.dotColor }} />
              {current.label}
            </button>
          </Menu.Target>
        </span>
      </Tooltip>
      <Menu.Dropdown onClick={stopRowClick} style={{ padding: 4, minWidth: 170, borderRadius: 8 }}>
        {LEAD_STATUS_OPTIONS.map((option) => (
          <Menu.Item
            key={option.status}
            onClick={() => option.status !== status && onChange(option.status)}
            leftSection={<span className="lead-status-dot" style={{ background: option.dotColor, marginTop: 5 }} />}
            style={{ alignItems: 'flex-start', padding: '6px 8px', borderRadius: 6 }}
          >
            <Text fz={13} lh="18px">
              {option.label}
            </Text>
            <Text fz={11} lh="15px" c={ORBIT_GRAY}>
              {option.hint}
            </Text>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
